import { useCallback, useEffect, useMemo, useState } from 'react';
import { createEmptyDraft } from '../utils/templateUtils';
import { getLibraryItemById } from '../constants';
import { validateTemplateDraft, validateSettingsPatch } from '../validation/templateValidation';
import {
  applyTriggerSelection,
  buildFlowFromTemplate,
  createNodeFromLibraryItem,
  defaultSelectedNode,
  extractBodyFromFlow,
  extractDmBodyFromFlow,
  insertFlowNodeAt,
  moveFlowNode,
  removeFlowNodeAt,
  canRemoveFlowNode,
  getRemoveBlockReason,
  findFlowNodeIndex,
  syncFlowFromDraft,
  syncFlowMessage,
  syncFlowNodeDetails,
} from '../utils/flowUtils';

export default function useTemplateEditor({ template, settings, onPreview, isNew = false }) {
  const [draft, setDraft] = useState(() => createEmptyDraft(template));
  const [localSettings, setLocalSettings] = useState(settings);
  const [flow, setFlow] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [errors, setErrors] = useState({});
  const [preview, setPreview] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sampleName, setSampleName] = useState('Alex');
  const [stepSaved, setStepSaved] = useState(false);

  useEffect(() => {
    const nextDraft = createEmptyDraft(template);
    setDraft(nextDraft);
    const built = buildFlowFromTemplate(template, settings);
    setFlow(built);
    setSelectedNode(defaultSelectedNode(built));
    setErrors({});
    setPreview('');
    setStepSaved(false);
  }, [template, settings]);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const updateDraft = useCallback((patch) => {
    setDraft((d) => {
      let next = { ...d, ...patch };

      if (patch.triggerLibraryId) {
        const { flow: nextFlow, draftPatch } = applyTriggerSelection(flow, patch.triggerLibraryId, next);
        next = { ...next, ...draftPatch };
        setFlow(nextFlow);
        setSelectedNode(nextFlow[0] || null);
      } else {
        setFlow((prev) => syncFlowNodeDetails(prev, next, localSettings));
      }

      return next;
    });
    setStepSaved(false);
    setErrors((e) => Object.fromEntries(Object.entries(e).filter(([k]) => !(k in patch))));
  }, [flow, localSettings]);

  const updateBody = useCallback((nextBody) => {
    setDraft((d) => {
      const next = { ...d, body: nextBody };
      setFlow((prev) => syncFlowMessage(prev, nextBody, 'body'));
      return next;
    });
    setErrors((e) => ({ ...e, body: undefined }));
    setStepSaved(false);
  }, []);

  const updateDmBody = useCallback((nextDmBody) => {
    setDraft((d) => {
      const next = { ...d, dmBody: nextDmBody };
      setFlow((prev) => syncFlowMessage(prev, nextDmBody, 'dmBody'));
      return next;
    });
    setStepSaved(false);
  }, []);

  const updateSettings = useCallback((patch) => {
    setLocalSettings((s) => {
      const nextSettings = { ...s, ...patch };
      setFlow((prev) => syncFlowNodeDetails(prev, draft, nextSettings));
      return nextSettings;
    });
  }, [draft]);

  const setTriggerFromLibrary = useCallback((item) => {
    setDraft((d) => {
      const { flow: nextFlow, draftPatch } = applyTriggerSelection(flow, item.id, d);
      const next = { ...d, ...draftPatch };
      setFlow(nextFlow);
      setSelectedNode(nextFlow[0] || null);
      return next;
    });
    setStepSaved(false);
  }, [flow]);

  const reorderFlow = useCallback((fromIndex, toIndex) => {
    setFlow((prev) => moveFlowNode(prev, fromIndex, toIndex));
    setStepSaved(false);
  }, []);

  const insertFromLibrary = useCallback((itemOrId, targetIndex) => {
    const item = typeof itemOrId === 'string' ? getLibraryItemById(itemOrId) : itemOrId;
    if (!item) return;

    if (item.kind === 'trigger') {
      setTriggerFromLibrary(item);
      return;
    }

    const node = createNodeFromLibraryItem(item, draft);
    if (!node) return;

    setFlow((prev) => insertFlowNodeAt(prev, node, targetIndex));
    setSelectedNode(node);

    if (node.id === 'lead') {
      setDraft((d) => {
        const next = { ...d, createLead: true, showAssignAgent: d.showAssignAgent !== false ? true : d.showAssignAgent };
        setFlow((prev) => syncFlowNodeDetails(prev, next, localSettings));
        return next;
      });
    }
    if (node.id === 'assign') {
      setDraft((d) => ({ ...d, showAssignAgent: true, assignAgent: d.assignAgent || 'Auto' }));
    }
    setStepSaved(false);
  }, [draft, localSettings, setTriggerFromLibrary]);

  const selectedNodeIndex = useMemo(
    () => findFlowNodeIndex(flow, selectedNode),
    [flow, selectedNode],
  );

  const removeBlockReason = useMemo(() => {
    if (selectedNodeIndex < 0) return '';
    if (canRemoveFlowNode(flow, selectedNodeIndex, draft)) return '';
    return getRemoveBlockReason(flow, selectedNodeIndex, draft);
  }, [flow, selectedNodeIndex, draft]);

  const canRemoveSelected = selectedNodeIndex >= 0
    && canRemoveFlowNode(flow, selectedNodeIndex, draft);

  const removeSelectedNode = useCallback(() => {
    if (selectedNodeIndex < 0) return false;
    const result = removeFlowNodeAt(flow, selectedNodeIndex, draft);
    if (!result.removed) return false;

    setFlow(result.flow);
    if (Object.keys(result.draftPatch).length) {
      setDraft((d) => {
        const next = { ...d, ...result.draftPatch };
        return next;
      });
    }
    setSelectedNode(defaultSelectedNode(result.flow));
    setStepSaved(false);
    return true;
  }, [draft, flow, selectedNodeIndex]);

  const validate = useCallback(() => {
    const draftResult = validateTemplateDraft(draft);
    const settingsResult = draft.templateType === 'custom'
      ? { valid: true, errors: {} }
      : validateSettingsPatch(localSettings);
    const merged = { ...draftResult.errors, ...settingsResult.errors };
    setErrors(merged);
    return draftResult.valid && settingsResult.valid;
  }, [draft, localSettings]);

  const buildSavePayload = useCallback(() => ({
    isNew,
    id: template.id,
    templateType: draft.templateType,
    name: draft.name,
    body: extractBodyFromFlow(flow, draft.templateType, draft.triggerChannel) || draft.body,
    dmBody: extractDmBodyFromFlow(flow) || draft.dmBody,
    status: draft.status,
    triggerChannel: draft.triggerChannel,
    triggerLibraryId: draft.triggerLibraryId,
    keywords: Array.isArray(draft.keywords)
      ? draft.keywords
      : String(draft.keywords || '').split(',').map((k) => k.trim()).filter(Boolean),
    matchRule: draft.matchRule,
    createLead: draft.createLead,
    isChannelDefault: !!draft.isChannelDefault,
    enableVaanikAgent: !!draft.enableVaanikAgent,
    showAssignAgent: draft.showAssignAgent !== false,
    settingsPatch: draft.templateType === 'custom' ? undefined : {
      createLeadOnComment: localSettings.createLeadOnComment,
      createLeadOnDm: localSettings.createLeadOnDm,
      leadStageOnComment: localSettings.leadStageOnComment,
      leadStageOnDm: localSettings.leadStageOnDm,
    },
  }), [draft, flow, isNew, localSettings, template.id]);

  const runPreview = useCallback(async () => {
    if (!validate()) return;
    setPreviewLoading(true);
    try {
      const body = draft.templateType === 'custom' && draft.triggerChannel === 'instagram_comment'
        ? (draft.dmBody || draft.body)
        : draft.body;
      const rendered = await onPreview(draft.templateType, body, sampleName);
      setPreview(rendered);
    } catch {
      setPreview('Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  }, [validate, onPreview, draft.templateType, draft.body, draft.dmBody, draft.triggerChannel, sampleName]);

  const saveStep = useCallback(() => {
    setFlow((prev) => syncFlowFromDraft(prev, draft, localSettings));
    setStepSaved(true);
    setTimeout(() => setStepSaved(false), 2000);
  }, [draft, localSettings]);

  const isValid = useMemo(() => {
    const draftResult = validateTemplateDraft(draft);
    const settingsResult = draft.templateType === 'custom'
      ? { valid: true }
      : validateSettingsPatch(localSettings);
    return draftResult.valid && settingsResult.valid;
  }, [draft, localSettings]);

  const selectedTriggerId = draft.triggerLibraryId
    || (draft.triggerChannel === 'instagram_dm' ? 'ig-dm' : 'ig-comment');

  return {
    draft,
    localSettings,
    flow,
    selectedNode,
    setSelectedNode,
    selectedTriggerId,
    errors,
    preview,
    previewLoading,
    sampleName,
    setSampleName,
    updateBody,
    updateDmBody,
    updateDraft,
    updateSettings,
    setTriggerFromLibrary,
    reorderFlow,
    insertFromLibrary,
    removeSelectedNode,
    canRemoveSelected,
    removeBlockReason,
    validate,
    buildSavePayload,
    runPreview,
    saveStep,
    stepSaved,
    isValid,
    setPreview,
    isNew,
  };
}
